package com.havn.app.features.reports.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.reports.domain.PropertyReportFeatureInterest
import com.havn.app.features.reports.domain.PropertyReportOpenHouseInsight
import com.havn.app.features.reports.domain.SellerReport
import com.havn.app.features.reports.domain.SellerReportPreview
import com.havn.app.features.reports.domain.SellerReportStatus
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun SellerReportsScreen(viewModel: SellerReportsViewModel, onBackToToday: (() -> Unit)? = null) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = SellerReportsMode::navigationDepth
    ) { mode ->
        HavnScreenFrame(
            eyebrow = "",
            title = when (mode) {
                SellerReportsMode.List -> strings.sellerReportsTitle
                SellerReportsMode.Generate -> strings.createReport
                SellerReportsMode.Preview -> strings.reportPreview
            },
            subtitle = "",
            backLabel = when (mode) {
                SellerReportsMode.List -> onBackToToday?.let { strings.navToday }
                SellerReportsMode.Generate -> strings.navReports
                SellerReportsMode.Preview -> strings.navReports
            },
            onBack = when (mode) {
                SellerReportsMode.List -> onBackToToday
                SellerReportsMode.Generate -> viewModel::openList
                SellerReportsMode.Preview -> viewModel::openList
            },
            isRefreshing = state.isLoading,
            onRefresh = viewModel::refresh
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                state.error?.let { message ->
                    HavnCard {
                        Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyLarge)
                        Spacer(Modifier.height(10.dp))
                        HavnButton(
                            text = strings.commonDismiss,
                            onClick = viewModel::clearError,
                            style = HavnButtonStyle.Ghost,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                when (mode) {
                    SellerReportsMode.List -> SellerReportScreen(state, viewModel)
                    SellerReportsMode.Generate -> SellerReportGeneratorScreen(state, viewModel)
                    SellerReportsMode.Preview -> ReportPreviewScreen(state, viewModel)
                }
            }
        }
    }
}

@Composable
fun SellerReportScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel
) {
    val strings = LocalHavnStrings.current
    var searchQuery by remember { mutableStateOf("") }
    var statusFilter by remember { mutableStateOf(ReportStatusFilter.All) }
    val filteredReports = remember(state.reports, searchQuery, statusFilter) {
        state.reports
            .filter { report -> statusFilter.matches(report) }
            .filter { report -> report.matchesSearch(searchQuery) }
    }

    if (state.isLoading) {
        LoadingState(strings.loadingSellerReports)
    } else if (state.reports.isEmpty()) {
        EmptyState(
            title = strings.noSellerReportsTitle,
            body = strings.noSellerReportsBody,
            actionText = strings.navProperties,
            onAction = {}
        )
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            HavnSearchField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = "Search seller reports"
            )
            ReportFilters(
                selected = statusFilter,
                reports = state.reports,
                onSelected = { statusFilter = it }
            )
            if (filteredReports.isEmpty()) {
                EmptyState(
                    title = "No matching reports",
                    body = "Try a different search or status filter.",
                    actionText = "Clear filters",
                    onAction = {
                        searchQuery = ""
                        statusFilter = ReportStatusFilter.All
                    }
                )
            } else {
                filteredReports.forEach { report ->
                    SavedReportRow(report = report, onClick = { viewModel.openReport(report.id) })
                }
            }
        }
    }
}

@Composable
private fun SellerReportGeneratorScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.chooseOpenHouse, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (state.openHouses.isEmpty()) {
                Text(strings.noOpenHousesAvailable, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            } else {
                state.openHouses.forEach { openHouse ->
                    OpenHousePickerRow(
                        openHouse = openHouse,
                        selected = state.selectedOpenHouseId == openHouse.id,
                        onClick = { viewModel.selectOpenHouse(openHouse.id) }
                    )
                }
            }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.agentNotes, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            HavnTextField(
                value = state.agentNotes,
                onValueChange = viewModel::updateAgentNotes,
                label = strings.notesForSeller,
                placeholder = strings.sellerNotesPlaceholder,
                singleLine = false,
                leadingIcon = HavnIconKey.Mail
            )
            HavnButton(
                text = strings.generatePreview,
                onClick = viewModel::generatePreview,
                loading = state.isGenerating,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Chart
            )
        }
    }
}

@Composable
fun ReportPreviewScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel
) {
    val strings = LocalHavnStrings.current
    val preview = state.preview
    if (preview == null) {
        if (state.isLoading) {
            LoadingState("Opening property report...")
            return
        }
        if (state.isGenerating) {
            LoadingState("Creating property report...")
            return
        }
        EmptyState(
            title = strings.noPreviewReadyTitle,
            body = strings.noPreviewReadyBody,
            actionText = strings.createReport,
            onAction = viewModel::openGenerator
        )
        return
    }
    val reportHistory = state.reports
        .filter { report ->
            preview.propertyId != null && report.propertyId == preview.propertyId
        }

    ExportReadyReportLayout(
        preview = preview,
        reportHistory = reportHistory,
        selectedReportId = preview.reportId,
        onSummaryChange = viewModel::updatePreviewSummary,
        onRecommendationsChange = viewModel::updatePreviewRecommendations,
        onNextStepsChange = viewModel::updatePreviewNextSteps,
        onStatusSelected = viewModel::updatePreviewStatus,
        onReportHistoryClick = viewModel::openReport
    )

    HavnButton(
        text = "Delete Report",
        onClick = viewModel::deletePreviewReport,
        loading = state.isDeletingReport,
        style = HavnButtonStyle.Danger,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Trash
    )
}

@Composable
private fun ReportStatusSegment(
    selected: SellerReportStatus,
    onSelected: (SellerReportStatus) -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val compact = maxWidth < 420.dp
        if (compact) {
            Column(
                verticalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    SellerReportStatus.entries.take(2).forEach { status ->
                        HavnListFilterChip(
                            label = status.label,
                            selected = selected == status,
                            onClick = { onSelected(status) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
                HavnListFilterChip(
                    label = SellerReportStatus.Archived.label,
                    selected = selected == SellerReportStatus.Archived,
                    onClick = { onSelected(SellerReportStatus.Archived) },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        } else {
            Row(
                horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
                modifier = Modifier.fillMaxWidth()
            ) {
                SellerReportStatus.entries.forEach { status ->
                    HavnListFilterChip(
                        label = status.label,
                        selected = selected == status,
                        onClick = { onSelected(status) },
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        }
    }
}

@Composable
private fun ExportReadyReportLayout(
    preview: SellerReportPreview,
    reportHistory: List<SellerReport>,
    selectedReportId: String?,
    onSummaryChange: (String) -> Unit,
    onRecommendationsChange: (String) -> Unit,
    onNextStepsChange: (String) -> Unit,
    onStatusSelected: (SellerReportStatus) -> Unit,
    onReportHistoryClick: (String) -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val wide = havnIsWebShell && maxWidth >= 1040.dp
        if (wide && preview.propertyId != null) {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                PropertySellerReportMain(preview, onSummaryChange, onRecommendationsChange, onNextStepsChange, Modifier.weight(1f))
                PropertySellerReportSidebar(
                    preview = preview,
                    reportHistory = reportHistory,
                    selectedReportId = selectedReportId,
                    onStatusSelected = onStatusSelected,
                    onReportHistoryClick = onReportHistoryClick,
                    modifier = Modifier.widthIn(min = 280.dp, max = 340.dp)
                )
            }
        } else if (preview.propertyId != null) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                PropertySellerReportMain(preview, onSummaryChange, onRecommendationsChange, onNextStepsChange, Modifier.fillMaxWidth())
                PropertySellerReportSidebar(
                    preview = preview,
                    reportHistory = reportHistory,
                    selectedReportId = selectedReportId,
                    onStatusSelected = onStatusSelected,
                    onReportHistoryClick = onReportHistoryClick,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        } else {
            LegacyOpenHouseReportLayout(preview, onSummaryChange, onRecommendationsChange, onNextStepsChange)
        }
    }
}

@Composable
private fun PropertySellerReportMain(
    preview: SellerReportPreview,
    onSummaryChange: (String) -> Unit,
    onRecommendationsChange: (String) -> Unit,
    onNextStepsChange: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    BoxWithConstraints(modifier) {
        val wide = maxWidth >= 860.dp
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        HavnCard {
            if (wide) {
                Row(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalAlignment = Alignment.Top) {
                    PropertyReportHeroText(preview, Modifier.weight(1f))
                    ReportPropertyPhoto(preview.propertyImageDataUrl, Modifier.weight(1.05f).height(210.dp).clip(MaterialTheme.shapes.medium))
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    ReportPropertyPhoto(preview.propertyImageDataUrl, Modifier.fillMaxWidth().height(170.dp).clip(MaterialTheme.shapes.medium))
                    PropertyReportHeroText(preview, Modifier.fillMaxWidth())
                }
            }
        }

        HavnCard {
            if (wide) {
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    ReportMetric("Connected Openings", preview.openHouseCount.toString(), Modifier.weight(1f))
                    ReportMetric("Total Visitors", preview.totalVisitors.toString(), Modifier.weight(1f))
                    ReportMetric("Connected Leads", preview.interestedBuyers.toString(), Modifier.weight(1f))
                    ReportMetric("Hot Leads Now", preview.hotLeads.toString(), Modifier.weight(1f))
                    ReportMetric("Tour Signals", preview.privateTours.toString(), Modifier.weight(1f))
                    ScoreBlock(preview.sellerConfidenceScore, preview.healthScoreLabel, Modifier.weight(1f))
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        ReportMetric("Connected Openings", preview.openHouseCount.toString(), Modifier.weight(1f))
                        ReportMetric("Visitors", preview.totalVisitors.toString(), Modifier.weight(1f))
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        ReportMetric("Connected Leads", preview.interestedBuyers.toString(), Modifier.weight(1f))
                        ReportMetric("Hot Leads Now", preview.hotLeads.toString(), Modifier.weight(1f))
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        ReportMetric("Tour Signals", preview.privateTours.toString(), Modifier.weight(1f))
                        ScoreBlock(preview.sellerConfidenceScore, preview.healthScoreLabel, Modifier.weight(1f))
                    }
                }
            }
        }

        if (wide) {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                PropertyFunnelCard(preview, Modifier.weight(0.85f))
                PropertyTimelineCard(preview.timeline, Modifier.weight(1.35f))
            }
        } else {
            PropertyFunnelCard(preview, Modifier.fillMaxWidth())
            PropertyTimelineCard(preview.timeline, Modifier.fillMaxWidth())
        }

        OpenHouseBreakdownCard(preview.timeline)

        if (wide) {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                FeatureScoresCard("What Buyers Loved", preview.featureInterest, Modifier.weight(1f), Color(0xFF4ADE80))
                FeatureScoresCard("Top Concerns", preview.concerns, Modifier.weight(1f), Color(0xFFF97316))
                AiBriefingCard(preview, Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                AgentActivityCard(preview, Modifier.weight(1f))
                RecommendationsCard(preview, onRecommendationsChange, Modifier.weight(1f))
                MarketPositionCard(preview, Modifier.weight(1f))
                OfferReadinessCard(preview, Modifier.weight(1f))
            }
        } else {
            FeatureScoresCard("What Buyers Loved", preview.featureInterest, Modifier.fillMaxWidth(), Color(0xFF4ADE80))
            FeatureScoresCard("Top Concerns", preview.concerns, Modifier.fillMaxWidth(), Color(0xFFF97316))
            AiBriefingCard(preview, Modifier.fillMaxWidth())
            AgentActivityCard(preview, Modifier.fillMaxWidth())
            RecommendationsCard(preview, onRecommendationsChange, Modifier.fillMaxWidth())
            MarketPositionCard(preview, Modifier.fillMaxWidth())
            OfferReadinessCard(preview, Modifier.fillMaxWidth())
        }

        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text("Executive Summary", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                HavnTextField(
                    value = preview.summaryText,
                    onValueChange = onSummaryChange,
                    label = "Executive summary",
                    placeholder = "Executive summary",
                    singleLine = false,
                    leadingIcon = HavnIconKey.Reports
                )
                Text("Buyer Insights", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                Text(preview.keyBuyerPatterns, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
                Text("Returning buyers: ${preview.returningBuyers}", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                Text("Next Steps", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                HavnTextField(
                    value = preview.nextSteps,
                    onValueChange = onNextStepsChange,
                    label = "Next steps",
                    placeholder = "Next steps",
                    singleLine = false,
                    leadingIcon = HavnIconKey.Check
                )
            }
        }
        }
    }
}

@Composable
private fun PropertyReportHeroText(preview: SellerReportPreview, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(preview.propertyTitle.ifBlank { preview.propertyAddress }, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
        Text(preview.propertyAddress, color = theme.primaryAccent, style = MaterialTheme.typography.titleMedium)
        Text("Generated ${preview.reportGeneratedAt.compactDateTimeLabel()} from openings, visitors, leads, follow-ups, and feedback connected to this property at report time.", color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            SmallPill(preview.propertyPrice.ifBlank { "Price not set" })
            SmallPill("${preview.propertyStatus} for ${preview.daysActive} days")
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            SmallPill("${preview.openHouseCount} connected opening${if (preview.openHouseCount == 1) "" else "s"}")
            SmallPill("${preview.interestedBuyers} connected lead${if (preview.interestedBuyers == 1) "" else "s"}")
        }
    }
}

@Composable
private fun LegacyOpenHouseReportLayout(
    preview: SellerReportPreview,
    onSummaryChange: (String) -> Unit,
    onRecommendationsChange: (String) -> Unit,
    onNextStepsChange: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Column {
                Text(preview.reportTitle, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                Text(preview.propertyAddress, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                Text(preview.openHouseDateTime, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                if (preview.isAiDraft) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "AI draft only. Review and edit before sending to the seller.",
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                ReportMetric(strings.commonVisitors, preview.totalVisitors.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonHot, preview.hotLeads.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonWarm, preview.warmLeads.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonCold, preview.coldLeads.toString(), Modifier.weight(1f))
            }

            if (preview.sellerConfidenceScore > 0) {
                ReportMetric("Seller confidence", "${preview.sellerConfidenceScore}/100")
                ReportSection("Confidence explanation", preview.sellerConfidenceExplanation)
            }

            EditableReportSection("Executive summary", preview.summaryText, onSummaryChange)
            ReportSection("Buyer interest overview", preview.buyerSentimentSummary)
            ReportSection("What buyers loved", preview.mostLovedFeatures)
            ReportSection("Buyer concerns", preview.commonConcerns)
            ReportSection("Lead quality summary", preview.leadQualitySummary.ifBlank { preview.summaryText })
            ReportSection("Agent activity", preview.agentActivitySummary.ifBlank { "${preview.followUpsSent} follow-ups sent." })
            EditableReportSection("AI recommendations", preview.recommendations, onRecommendationsChange)
            ReportSection("Market position comparison", preview.marketPositionComparison)
            EditableReportSection("Next steps", preview.nextSteps, onNextStepsChange)
        }
    }
}

@Composable
private fun ReportMetric(label: String, value: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier) {
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
    }
}

@Composable
private fun PropertySellerReportSidebar(
    preview: SellerReportPreview,
    reportHistory: List<SellerReport>,
    selectedReportId: String?,
    onStatusSelected: (SellerReportStatus) -> Unit,
    onReportHistoryClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(14.dp)) {
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("CONNECTED TO PROPERTY", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                ReportPropertyPhoto(preview.propertyImageDataUrl, Modifier.fillMaxWidth().height(92.dp).clip(MaterialTheme.shapes.medium))
                Text(preview.propertyTitle.ifBlank { preview.propertyAddress }, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                Text(preview.propertyAddress, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                Text("Report date: ${preview.reportGeneratedAt.compactDateTimeLabel()}", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                HavnButton("View Property", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Home)
            }
        }
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("DATA ROLLUP", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                SidebarMetric("Connected openings", preview.openHouseCount)
                SidebarMetric("Visitors", preview.totalVisitors)
                SidebarMetric("Connected leads", preview.interestedBuyers)
                SidebarMetric("Hot leads", preview.hotLeads)
                SidebarMetric("Follow-ups", preview.followUpsSent)
                SidebarMetric("Reports", preview.reportsCount)
            }
        }
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                ReportStatusSegment(
                    selected = preview.status,
                    onSelected = onStatusSelected
                )
                Text("ACTIONS", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                HavnButton("Generate AI Insights", onClick = {}, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Star)
                HavnButton("Preview PDF", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Reports)
                HavnButton("Share Report", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Send)
                HavnButton("Download PDF", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.ArrowRight)
                HavnButton("Copy Share Link", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Mail)
                HavnButton("Send to Seller", onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Check)
            }
        }
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("REPORT HISTORY", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                if (reportHistory.isEmpty()) {
                    Text("No previous reports yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                } else {
                    reportHistory.take(5).forEach { report ->
                        ReportHistoryButton(
                            report = report,
                            selected = report.id == selectedReportId,
                            onClick = { onReportHistoryClick(report.id) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportHistoryButton(
    report: SellerReport,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        color = if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 10.dp, vertical = 9.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                HavnIconKey.Reports,
                contentDescription = null,
                tint = if (selected) theme.primaryAccent else theme.secondaryText,
                modifier = Modifier.size(16.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    report.createdAt.compactDateLabel(),
                    color = if (selected) theme.primaryAccent else theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    report.status.label,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun PropertyHeroPlaceholder(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier.background(
            Brush.linearGradient(
                listOf(Color(0xFF0F172A), Color(0xFF1D4ED8), Color(0xFFF59E0B))
            ),
            shape = MaterialTheme.shapes.medium
        )
    )
}

@Composable
private fun SmallPill(text: String) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.card, shape = MaterialTheme.shapes.small, border = BorderStroke(1.dp, theme.border)) {
        Text(text, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.padding(horizontal = 10.dp, vertical = 7.dp))
    }
}

@Composable
private fun ScoreBlock(score: Int, label: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(score.toString(), color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
        Text("/100", color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        Text(label, color = theme.primaryAccent, style = MaterialTheme.typography.labelSmall, maxLines = 2, overflow = TextOverflow.Ellipsis)
    }
}

@Composable
private fun PropertyFunnelCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("PROPERTY LEAD FUNNEL", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            FunnelStage("Visitors", preview.totalVisitors, Color(0xFF2563EB), 1f)
            FunnelStage("Interested Buyers", preview.interestedBuyers, Color(0xFF10B981), 0.82f)
            FunnelStage("Hot Leads", preview.hotLeads, Color(0xFFF59E0B), 0.64f)
            FunnelStage("Tour Signals", preview.privateTours, Color(0xFF8B5CF6), 0.48f)
            FunnelStage("Offer Signals", preview.offers, Color(0xFFDB2777), 0.35f)
            Text("View full funnel analysis ->", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun FunnelStage(label: String, value: Int, color: Color, fraction: Float) {
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Surface(modifier = Modifier.fillMaxWidth(fraction).height(44.dp), color = color.copy(alpha = 0.82f), shape = MaterialTheme.shapes.small) {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                Text(value.toString(), color = Color.White, style = MaterialTheme.typography.titleMedium)
                Text(label, color = Color.White, style = MaterialTheme.typography.labelSmall)
            }
        }
    }
}

@Composable
private fun PropertyTimelineCard(timeline: List<PropertyReportOpenHouseInsight>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("PROPERTY TIMELINE", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val wide = maxWidth >= 620.dp
                if (wide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        timeline.take(4).forEach { item ->
                            TimelineMiniCard(item, Modifier.weight(1f))
                        }
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        timeline.take(4).forEach { item -> TimelineMiniCard(item, Modifier.fillMaxWidth()) }
                    }
                }
            }
            Text("Timeline is based on connected open house activity ->", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun TimelineMiniCard(item: PropertyReportOpenHouseInsight, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(item.date, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            Text(item.title, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text("${item.visitors} visitors · ${item.hotLeads} hot leads", color = theme.primaryText, style = MaterialTheme.typography.labelSmall)
            ScoreBlock(item.sentimentScore, "Sentiment")
        }
    }
}

@Composable
private fun OpenHouseBreakdownCard(timeline: List<PropertyReportOpenHouseInsight>) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("OPEN HOUSE BREAKDOWN", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            timeline.forEach { item ->
                Surface(color = theme.surface, shape = MaterialTheme.shapes.medium, border = BorderStroke(1.dp, theme.border), modifier = Modifier.fillMaxWidth()) {
                    Column(Modifier.padding(10.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(item.title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                            Text(item.date, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                            ReportMetaPill("${item.visitors} visitors")
                            ReportMetaPill("${item.interestedBuyers} interested")
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                            ReportMetaPill("${item.hotLeads} hot")
                            ReportMetaPill("${item.privateTours} tours")
                            ReportMetaPill("${item.sentimentScore}% sentiment")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FeatureScoresCard(title: String, scores: List<PropertyReportFeatureInterest>, modifier: Modifier, accent: Color) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(title.uppercase(), color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            scores.take(5).forEach { score ->
                ScoreLine(score.label, score.score, accent)
            }
            Text("View all feedback ->", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun ScoreLine(label: String, value: Int, accent: Color) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            Text("$value%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
        }
        Surface(modifier = Modifier.fillMaxWidth().height(4.dp), color = theme.border.copy(alpha = 0.45f), shape = MaterialTheme.shapes.small) {
            Box(Modifier.fillMaxWidth(value / 100f).fillMaxSize().background(accent))
        }
    }
}

@Composable
private fun AiBriefingCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                HavnIcon(HavnIconKey.Star, "AI", tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                Text("AI EXECUTIVE BRIEFING", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            Text(preview.buyerSentimentSummary, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
            Text("View all AI insights ->", color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun AgentActivityCard(preview: SellerReportPreview, modifier: Modifier) {
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(9.dp)) {
            Text("AGENT ACTIVITY", color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleMedium)
            ActivityLine("Follow-Ups Sent", preview.followUpsSent)
            ActivityLine("Tour Signals", preview.privateTours)
            ActivityLine("Financing Packets Shared", preview.financingInfoShared)
            ActivityLine("Buyer Calls", preview.callsMade)
        }
    }
}

@Composable
private fun ActivityLine(label: String, value: Int) {
    val theme = LocalHavnThemeTokens.current
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
        Text(value.toString(), color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun RecommendationsCard(preview: SellerReportPreview, onChange: (String) -> Unit, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("AI RECOMMENDATIONS", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            HavnTextField(
                value = preview.recommendations,
                onValueChange = onChange,
                label = "Recommendations",
                placeholder = "Recommendations",
                singleLine = false,
                leadingIcon = HavnIconKey.Star
            )
        }
    }
}

@Composable
private fun MarketPositionCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(9.dp)) {
            Text("MARKET POSITION", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(preview.marketPositionComparison, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            Text("${preview.totalVisitors} visitors across ${preview.openHouseCount} open houses", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
            Text("${preview.hotLeads} hot leads from connected registrations", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
            Text("${preview.privateTours} tour signals from follow-up/feedback text", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun OfferReadinessCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text("OFFER READINESS SCORE", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text("${preview.offerReadinessScore}%", color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
            Text(preview.offerReadinessExplanation, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun SidebarMetric(label: String, value: Int) {
    val theme = LocalHavnThemeTokens.current
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Text(value.toString(), color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun ReportSection(title: String, body: String) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(5.dp))
        Text(body, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun EditableReportSection(title: String, body: String, onChange: (String) -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(5.dp))
        HavnTextField(
            value = body,
            onValueChange = onChange,
            label = title,
            placeholder = title,
            singleLine = false,
            leadingIcon = HavnIconKey.Reports
        )
    }
}

@Composable
private fun OpenHousePickerRow(
    openHouse: OpenHouse,
    selected: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    HavnButton(
        text = "${openHouse.propertyTitle} · ${openHouse.startsAt.compactDateTime(strings)}",
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Calendar
    )
}

@Composable
private fun ReportFilters(
    selected: ReportStatusFilter,
    reports: List<SellerReport>,
    onSelected: (ReportStatusFilter) -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val wide = maxWidth >= 620.dp
        if (wide) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                ReportStatusFilter.entries.forEach { filter ->
                    ReportFilterChip(
                        label = filter.label,
                        selected = selected == filter,
                        onClick = { onSelected(filter) },
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    ReportStatusFilter.entries.take(2).forEach { filter ->
                        ReportFilterChip(
                            label = filter.label,
                            selected = selected == filter,
                            onClick = { onSelected(filter) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    ReportStatusFilter.entries.drop(2).forEach { filter ->
                        ReportFilterChip(
                            label = filter.label,
                            selected = selected == filter,
                            onClick = { onSelected(filter) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    HavnListFilterChip(
        label = label,
        selected = selected,
        onClick = onClick,
        modifier = modifier
    )
}

@Composable
private fun SavedReportRow(report: SellerReport, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.Top
        ) {
            Box(
                modifier = Modifier
                    .width(96.dp)
                    .height(102.dp)
                    .clip(MaterialTheme.shapes.medium)
            ) {
                ReportPropertyPhoto(report.propertyThumbnailDataUrl, Modifier.fillMaxSize())
                ReportStatusBadge(
                    status = report.status,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(7.dp)
                )
            }
            Column(
                modifier = Modifier.weight(1f).height(102.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        report.propertyTitle?.takeIf { it.isNotBlank() } ?: "Property Performance Report",
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        report.propertyAddress?.takeIf { it.isNotBlank() } ?: report.summary?.take(80) ?: strings.openHouseReport,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ReportMetricItem(
                        icon = HavnIconKey.Users,
                        value = report.visitorCount.toString(),
                        label = "Visitors",
                        modifier = Modifier.weight(1f)
                    )
                    ReportMetricItem(
                        icon = HavnIconKey.Leads,
                        value = report.hotCount.toString(),
                        label = "Hot",
                        modifier = Modifier.weight(1f)
                    )
                    ReportMetricItem(
                        icon = HavnIconKey.Calendar,
                        value = report.createdAt.compactDateLabel(),
                        label = "Date",
                        modifier = Modifier.weight(1.15f)
                    )
                }
            }
        }
    }
}

@Composable
private fun ReportPropertyPhoto(dataUrl: String?, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Box(modifier = modifier) {
        if (!dataUrl.isNullOrBlank()) {
            HavnDatabaseImage(
                dataUrl = dataUrl,
                contentDescription = "Property photo",
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Brush.linearGradient(listOf(theme.surface, theme.gradientStart, theme.primaryAccent.copy(alpha = 0.35f)))),
                contentAlignment = Alignment.Center
            ) {
                HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(26.dp))
            }
        }
    }
}

@Composable
private fun ReportStatusBadge(status: SellerReportStatus, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    val background = when (status) {
        SellerReportStatus.Draft -> theme.primaryAccent.copy(alpha = 0.86f)
        SellerReportStatus.Accepted -> Color(0xFF22C55E)
        SellerReportStatus.Archived -> theme.secondaryText.copy(alpha = 0.86f)
    }
    val contentColor = background.readableBadgeTextColor()
    val labelStyle = MaterialTheme.typography.labelLarge.copy(
        fontSize = when {
            status.label.length > 10 -> 9.sp
            status.label.length > 8 -> 10.sp
            else -> MaterialTheme.typography.labelLarge.fontSize
        }
    )
    Surface(
        modifier = modifier.widthIn(max = 82.dp),
        color = background,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, background.copy(alpha = 0.95f))
    ) {
        Text(
            status.label,
            color = contentColor,
            style = labelStyle,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ReportMetricItem(
    icon: HavnIconKey,
    value: String,
    label: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                icon = icon,
                contentDescription = null,
                modifier = Modifier.size(14.dp),
                tint = theme.secondaryText
            )
            Text(
                value,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            label,
            color = theme.mutedText,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ReportMetaPill(text: String) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.surface, shape = MaterialTheme.shapes.small, border = BorderStroke(1.dp, theme.border)) {
        Text(
            text,
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.padding(horizontal = 9.dp, vertical = 5.dp),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private fun String?.compactDateLabel(): String {
    if (this.isNullOrBlank()) return "-"
    val date = substringBefore("T").takeIf { it.isNotBlank() } ?: return "-"
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = when (parts[1]) {
        "01" -> "Jan"
        "02" -> "Feb"
        "03" -> "Mar"
        "04" -> "Apr"
        "05" -> "May"
        "06" -> "Jun"
        "07" -> "Jul"
        "08" -> "Aug"
        "09" -> "Sep"
        "10" -> "Oct"
        "11" -> "Nov"
        else -> "Dec"
            }
    return "$month ${parts[2].trimStart('0').ifBlank { "1" }}"
}

private fun String?.compactDateTimeLabel(): String {
    if (this.isNullOrBlank()) return "just now"
    val clean = replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
    val parts = clean.split(" ")
    val date = parts.getOrNull(0).compactDateLabel()
    val time = parts.getOrNull(1)
        ?.split(":")
        ?.take(2)
        ?.joinToString(":")
        ?.takeIf { it.isNotBlank() }
    return listOfNotNull(date.takeIf { it != "-" }, time).joinToString(" at ").ifBlank { clean }
}

private fun Color.readableBadgeTextColor(): Color {
    val luminance = (0.299f * red) + (0.587f * green) + (0.114f * blue)
    return if (luminance > 0.58f) Color.Black else Color.White
}

private enum class ReportStatusFilter(val label: String) {
    All("All"),
    Draft("Draft"),
    Accepted("Accepted"),
    Archived("Archived");

    fun matches(report: SellerReport): Boolean = when (this) {
        All -> true
        Draft -> report.status == SellerReportStatus.Draft
        Accepted -> report.status == SellerReportStatus.Accepted
        Archived -> report.status == SellerReportStatus.Archived
    }

    fun countFor(reports: List<SellerReport>): Int = reports.count { matches(it) }
}

private fun SellerReport.matchesSearch(query: String): Boolean {
    val normalized = query.trim()
    if (normalized.isBlank()) return true
    return listOfNotNull(
        propertyTitle,
        propertyAddress,
        status.label,
        summary,
        sellerFeedback,
        nextSteps,
        createdAt
    ).any { it.contains(normalized, ignoreCase = true) }
}

private fun String.compactDateTime(strings: com.havn.app.core.localization.HavnStrings): String {
    if (isBlank()) return strings.commonNotScheduled
    return replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
}

private fun SellerReportsMode.navigationDepth(): Int = when (this) {
    SellerReportsMode.List -> 0
    SellerReportsMode.Generate -> 1
    SellerReportsMode.Preview -> 2
}
